/*
 * Detects whether a WorkSpace should open the agentic client instead of the
 * standard streaming client.
 *
 * The signal is a marker inside GetResources'
 * `Resources[0].ResourceDetails.WorkspaceCapabilities`, which is a stringified
 * JSON array of capability strings. The field placement and the marker value
 * below are a proposed convention and may change once the capability contract
 * is finalized.
 */
import {
  ClientStage,
  RegionResolver,
} from '@amzn/work-spaces-web-client-core-lib';

// Marker string expected inside the WorkspaceCapabilities array. Proposed value.
export const AGENTIC_CAPABILITY = 'WS4B_AGENTIC';

// Testing override: forces agentic mode without a real capability. Persisted in
// localStorage so it survives the sign-in redirect and the hard navigations back
// to the client. A production build honours it only on a preprod stage.
const FORCE_AGENTIC_STORAGE_KEY = 'ws4b_force_agentic';
const FORCE_AGENTIC_QUERY_PARAM = 'forceAgentic';

// Pipeline stages ship a production build, so the build mode alone cannot tell
// beta from prod. The serving hostname can.
const PREPROD_STAGES: ReadonlySet<string> = new Set([
  ClientStage.LOCAL,
  ClientStage.BETA,
  ClientStage.GAMMA,
]);

const isDevBuild = (): boolean => process.env.NODE_ENV !== 'production';

const isPreprodStage = (): boolean => {
  try {
    return PREPROD_STAGES.has(RegionResolver.resolveClientStage());
  } catch {
    // An unrecognised hostname could be a customer-facing one, so treat it as prod.
    return false;
  }
};

const isOverrideAllowed = (): boolean => isDevBuild() || isPreprodStage();

/**
 * Persists the `?forceAgentic=1` query flag into localStorage on first sight so
 * it outlives the multiple full-page navigations of the sign-in flow. No-op
 * where the override is not allowed and when the flag is absent. Safe to call
 * repeatedly.
 */
export const captureForceAgenticFlag = (): void => {
  if (!isOverrideAllowed()) {
    return;
  }
  try {
    const params = new URLSearchParams(window.location.search);
    if (params.get(FORCE_AGENTIC_QUERY_PARAM) === '1') {
      window.localStorage.setItem(FORCE_AGENTIC_STORAGE_KEY, '1');
    }
  } catch {
    // window/localStorage unavailable — nothing to capture.
  }
};

const isForceAgenticActive = (): boolean => {
  if (!isOverrideAllowed()) {
    return false;
  }
  captureForceAgenticFlag();
  const envFlag = process.env.REACT_APP_FORCE_AGENTIC;
  if (envFlag === '1' || envFlag === 'true') {
    return true;
  }
  try {
    return window.localStorage.getItem(FORCE_AGENTIC_STORAGE_KEY) === '1';
  } catch {
    return false;
  }
};

const parseWorkspaceCapabilities = (raw?: string | null): string[] => {
  if (!raw) {
    return [];
  }
  try {
    const parsed: unknown = JSON.parse(raw);
    if (!Array.isArray(parsed)) {
      return [];
    }
    return parsed.filter((entry): entry is string => typeof entry === 'string');
  } catch {
    return [];
  }
};

/**
 * Returns true when the WorkSpace advertises agentic capability, or when the
 * testing override is active (a non-production build, or a preprod stage).
 *
 * @param workspaceCapabilities the raw stringified capability array from
 *   `Resources[0].ResourceDetails.WorkspaceCapabilities`
 */
export const isAgenticWorkspace = (
  workspaceCapabilities?: string | null
): boolean => {
  if (isForceAgenticActive()) {
    return true;
  }
  return parseWorkspaceCapabilities(workspaceCapabilities).includes(
    AGENTIC_CAPABILITY
  );
};
